import { actions as segmentioActions } from '@saucelabs/sl-util-segmentio';

import {
  CONFIGURATION_DEVICE_TYPES,
  CONFIGURATION_TEST_TYPES,
  TEST_CONFIGURATION_TABS,
} from './constants';
import { ValueOf } from './globalTypes';

const MANUAL_PREFIX = 'Manual - ';
const MANUAL_TEST_PREFIX = 'Manual Test - ';

export const MANUAL_UI_ELEMENTS = Object.freeze({
  DEVICE_DETAIL_MODAL: 'Device Detail Modal',
  DEVICE_LIST_ITEM: 'Device List Item',
  DEVICE_SUMMARY: 'Device Summary',
  DEVICE_TILE: 'Device Tile',
  RECENT_AND_SAVED_CONFIGS_START_BUTTON: 'Recent and Saved Configs Start Button',
  RECENT_CONFIGS_LIST_ITEM: 'Recent Configs List Item',
  SAVED_CONFIGS_LIST_ITEM: 'Saved Configs List Item',
  VIRTUAL_DEVICE_CONFIG_START_BUTTON: 'Virtual Device Config Start Button',
});

export const MANUAL_EVENTS = Object.freeze({
  ADD_STARRED_DEVICE: `${MANUAL_PREFIX}Add Starred Device`,
  HIDE_PUBLIC_CLOUD_WARNING: `${MANUAL_PREFIX}Hide Public Cloud Warning`,
  FILTER_REAL_DEVICE: `${MANUAL_PREFIX}Device Filter`,
  NAVIGATE_TO_BILLING_FROM_TEST_SESSION: `${MANUAL_PREFIX}Navigate To Billing from Test Session Expiry Banner`,
  NAVIGATE_TO_MOBILE_VIRTUAL_TAB: `${MANUAL_PREFIX}Navigate to Mobile Virtual Tab`,
  RDC_GRID_OPENED: `${MANUAL_PREFIX}RDC Grid Opened`,
  RDC_GRID_VIEW_TYPE_CHANGED: `${MANUAL_PREFIX}RDC Grid View Type Changed`,
  REMOVE_STARRED_DEVICE: `${MANUAL_PREFIX}Remove Starred Device`,
  SEARCH_REAL_DEVICE: `${MANUAL_PREFIX}Device Search`,
  SELECT_APP: `${MANUAL_PREFIX}Select App`,
  SHOW_DEVICE_DETAILS: `${MANUAL_PREFIX}Show Device Details`,
  SHOW_PUBLIC_CLOUD_WARNING: `${MANUAL_PREFIX}Show Public Cloud Warning`,
  TAKE_RDC_SCREENSHOT: `${MANUAL_PREFIX}Take RDC Screenshot`,
  TEST_LAUNCHED: `${MANUAL_PREFIX}Test Launched`,
  END_SESSION_SAVE_BUTTON: `${MANUAL_PREFIX}End Session Save Button Clicked`,
});

export const MANUAL_TEST_EVENTS = Object.freeze({
  CLOSE_DEVICE_OVERLAY: `${MANUAL_TEST_PREFIX}Close Device Overlay`,
  COMPLETE_UPLOAD_FILE_TO_SD_CARD: `${MANUAL_PREFIX}Complete Upload File to SD Card`,
  INSTALL_DEPENDENT_APP: `${MANUAL_TEST_PREFIX}Install Dependent App`,
  RESTART_APP: `${MANUAL_TEST_PREFIX}Restart App`,
  SELECT_APP_VERSION: `${MANUAL_TEST_PREFIX}Select App Version`,
  SHOW_DEPENDENT_APP: `${MANUAL_TEST_PREFIX}Show Dependent App`,
  SHOW_SWITCH_APP_VERSION: `${MANUAL_TEST_PREFIX}Show Switch App Version`,
  SHOW_UPLOAD_FILES_TO_SD_CARD: `${MANUAL_TEST_PREFIX}Show Upload Files to SD Card`,
  START_UPLOAD_FILE_TO_SD_CARD: `${MANUAL_TEST_PREFIX}Start Upload File to SD Card`,
  SHOW_CAMERA_INJECTION_POPOVER: `${MANUAL_PREFIX}Show Camera Injection Popover`,
  SHOW_BIOMETRIC_AUTHENTICATION_POPOVER: `${MANUAL_PREFIX}Show Biometric Authentication Popover`,
  SHOW_DEVELOPER_OPTIONS: `${MANUAL_TEST_PREFIX}Show Developer Options`,
  HIDE_DEVELOPER_OPTIONS: `${MANUAL_TEST_PREFIX}Hide Developer Options`,
  SELECT_DEVICE_LOGS_TAB: `${MANUAL_TEST_PREFIX}Select Device Logs Tab`,
  SELECT_DEBUG_DEV_TOOLS_TAB: `${MANUAL_TEST_PREFIX}Select Debug Dev Tools Tab`,
  VIEW_LIVE_SESSION: `${MANUAL_TEST_PREFIX}View Live Session`,
});

export const NAVIGATION_EVENTS = Object.freeze({
  CONFIGURATION_TABS: Object.freeze({
    [TEST_CONFIGURATION_TABS.DESKTOP]: `${MANUAL_PREFIX}Show Desktop Config`,
    [TEST_CONFIGURATION_TABS.MOBILE_REAL]: `${MANUAL_PREFIX}Show Mobile Real Config`,
    [TEST_CONFIGURATION_TABS.MOBILE_VIRTUAL]: `${MANUAL_PREFIX}Show Mobile Virtual Config`,
  }),
});

export const EVENTS = Object.freeze({
  OBJECT_CLICKED: 'Object Clicked',
  MESSAGE_SHOWN: 'Message Shown',
});

export const CTA_EVENTS = Object.freeze({
  STOP_TEST: `${MANUAL_TEST_PREFIX}Stop Test`,
});

export const TRACKING_PROPS = Object.freeze({
  DRAG_AND_DROP: 'drag and drop',
  FILE_BROWSER: 'file browser',
});

export const LIVE_TESTING_TRACKING_PROPS = Object.freeze({
  product_area: 'Live Testing',
});

export const LIVE_TESTING_PRODUCT_SUB_AREAS = Object.freeze({
  END_SESSION_SCREEN: 'end session screen',
  CONFIG_PAGE: 'Config Page',
  RECENTS: 'Recents',
  RECENTS_START_TEST_BUTTON: 'Recents - Start Test Button',
});

export const LIVE_TESTING_SUBJECT_NAMES = Object.freeze({
  END_SESSION_REWATCH: 'End session Rewatch',
  END_SESSION_START_NEW_TEST: 'End session start new test',
  MANUAL_FREE_TRIAL_UPGRADE: 'Manual Free Trial Upgrade',
  RECENTS_BUTTON_CLICKED: 'Recents Button clicked',
  TEST_STARTED_FROM_RECENTS: 'Tests Started from Recents',
  TOGGLE_AUDIO: 'Toggle Audio',
  TOGGLE_PASSCODE: 'Toggle Passcode',
});

const TOOLBAR_TRACKING_PREFIX = `${MANUAL_PREFIX}Toolbar - ` as const;
export const TOOLBAR_TRACKING_NAMES = Object.freeze({
  ACCESSIBILITY_AUDIO: `${TOOLBAR_TRACKING_PREFIX}Accessibility Audio`,
  ADB_SHELL: `${TOOLBAR_TRACKING_PREFIX}ADB Shell`,
  ADB_SHELL_EXECUTE: `${TOOLBAR_TRACKING_PREFIX}ADB Shell - Execute`,
  ANIMATIONS: `${TOOLBAR_TRACKING_PREFIX}More Device Options - System Animations`,
  AUDIO: `${TOOLBAR_TRACKING_PREFIX}Audio`,
  CLIPBOARD: `${TOOLBAR_TRACKING_PREFIX}Clipboard`,
  DEVELOPERS_OPTIONS_CLEAR_LOG: `${TOOLBAR_TRACKING_PREFIX}Developers Options - Clear Log`,
  DEVELOPERS_OPTIONS_DOWNLOAD_LOG: `${TOOLBAR_TRACKING_PREFIX}Developers Options - Download Log`,
  DEVELOPERS_OPTIONS_PAUSE_LOG: `${TOOLBAR_TRACKING_PREFIX}Developers Options - Pause Log`,
  DEVELOPERS_OPTIONS_SEARCH_LOGS: `${TOOLBAR_TRACKING_PREFIX}Developers Options - Search Logs`,
  DEVELOPERS_OPTIONS_WRAP_TEXT: `${TOOLBAR_TRACKING_PREFIX}Developers Options - Wrap Text`,
  DEVELOPERS_OPTIONS_ALIGN_HORIZONTALLY: `${TOOLBAR_TRACKING_PREFIX}Developers Options - Align Horizontally`,
  DEVELOPERS_OPTIONS_ALIGN_VERTICALLY: `${TOOLBAR_TRACKING_PREFIX}Developers Options - Align Vertically`,
  LANGUAGE: `${TOOLBAR_TRACKING_PREFIX}More Device Options - Language`,
  PERFORMANCE_MODE: `${TOOLBAR_TRACKING_PREFIX}Performance Mode`,
  ROTATE_DEVICE: `${TOOLBAR_TRACKING_PREFIX}Rotate Device`,
  SCREENSHOT: `${TOOLBAR_TRACKING_PREFIX}Screenshot`,
  SESSION_INFO: `${TOOLBAR_TRACKING_PREFIX}Session Info`,
  SET_LOCATION: `${TOOLBAR_TRACKING_PREFIX}More Device Options - Set Location`,
  WIFI: `${TOOLBAR_TRACKING_PREFIX}More Device Options - Wifi`,
});

export const trackEvent = segmentioActions.recordTrack;

export default trackEvent;
